"use client";

import { useEffect, useState, useMemo, useRef } from "react";
import Image from "next/image";
import { useTranslations, useLocale } from "next-intl";
import { fetchDirectorio } from "@/lib/api/datosGenerales/fetchDirectorio";
import {
  DirectorioContacto,
  DirectorioCategoria,
} from "@/lib/api/datosGenerales/types/directorio";
import {
  Search,
  Phone,
  Mail,
  Building2,
  Users,
  Plane,
  Landmark,
  Briefcase,
  X,
  ChevronDown,
  Check,
  Loader2,
  LucideIcon,
} from "lucide-react";
import { type Locale } from "@/i18n/config";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Panor%C3%A1micas%20internas/Foto10-Panel-ExpoFotos-T2-Ratto-Panel08-antesydespues.jpg";

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "vice presidencia": Building2,
  "vice presidency": Building2,
  "gerencia o departamento": Briefcase,
  "management or department": Briefcase,
  "otras instituciones": Landmark,
  "other institutions": Landmark,
  "otros aeropuertos": Plane,
  "other airports": Plane,
};

function getCategoryIcon(name: string): LucideIcon {
  return CATEGORY_ICONS[name.toLowerCase().trim()] ?? Users;
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
  allLabel,
}: {
  label: string;
  value: number | null;
  options: { id: number; nombre: string }[];
  onChange: (value: number | null) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const selectedOption = options.find((o) => o.id === value);
  const displayValue = selectedOption?.nombre ?? allLabel;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center justify-between gap-3 min-w-[220px] px-4 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors border ${
          value !== null
            ? "bg-[#102542] text-white border-[#102542]"
            : "bg-white text-gray-700 border-gray-200 hover:border-[#102542]"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 truncate">
          <span
            className={`opacity-70 ${value !== null ? "text-white/80" : "text-gray-400"}`}
          >
            {label}:
          </span>
          <span className="truncate normal-case tracking-normal font-medium">
            {displayValue}
          </span>
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute top-full left-0 mt-2 z-40 min-w-full max-w-xs max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-lg py-2"
        >
          <button
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
              value === null
                ? "text-[#102542] font-semibold bg-gray-50"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span>{allLabel}</span>
            {value === null && <Check className="w-4 h-4" />}
          </button>
          <div className="my-1 border-t border-gray-100" />
          {options.map((opt) => (
            <button
              key={opt.id}
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between transition-colors ${
                value === opt.id
                  ? "text-[#102542] font-semibold bg-gray-50"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="truncate">{opt.nombre}</span>
              {value === opt.id && <Check className="w-4 h-4 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactCard({ contacto }: { contacto: DirectorioContacto }) {
  const Icon = getCategoryIcon(contacto.categoria.nombre);

  return (
    <article className="bg-white p-6 flex flex-col transition-colors hover:bg-gray-50">
      <div className="flex items-start gap-3 mb-5">
        <Icon className="w-5 h-5 text-[#102542] flex-shrink-0 mt-1" />
        <h3 className="font-semibold text-gray-900 leading-snug text-sm line-clamp-3">
          {contacto.nombre}
        </h3>
      </div>

      <div className="space-y-2 mt-auto">
        {contacto.telefono && (
          <a
            href={`tel:${contacto.telefono}`}
            className="flex items-center gap-2 text-xs text-gray-600 hover:text-[#102542] transition-colors group"
          >
            <Phone className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#102542] flex-shrink-0" />
            <span className="truncate">{contacto.telefono}</span>
          </a>
        )}
        {contacto.correo && (
          <a
            href={`mailto:${contacto.correo}`}
            className="flex items-center gap-2 text-xs text-gray-600 hover:text-[#102542] transition-colors group"
          >
            <Mail className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#102542] flex-shrink-0" />
            <span className="truncate">{contacto.correo}</span>
          </a>
        )}
      </div>
    </article>
  );
}

export default function DirectorioPage() {
  const t = useTranslations("directorioPage");
  const locale = useLocale() as Locale;

  const [contactos, setContactos] = useState<DirectorioContacto[]>([]);
  const [categorias, setCategorias] = useState<DirectorioCategoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await fetchDirectorio(locale);
        setContactos(data.contactos);
        setCategorias(data.categorias);
      } catch (error) {
        console.error("Error al cargar directorio:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [locale]);

  useEffect(() => {
    if (!searchQuery) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => setIsSearching(false), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredContactos = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return contactos.filter((c) => {
      const matchesSearch =
        q === "" ||
        c.nombre.toLowerCase().includes(q) ||
        c.categoria.nombre.toLowerCase().includes(q);
      const matchesCategory =
        selectedCategory === null || c.categoria.id === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [contactos, searchQuery, selectedCategory]);

  const groupedContactos = useMemo(() => {
    const groups = new Map<
      number,
      { categoria: DirectorioCategoria; contactos: DirectorioContacto[] }
    >();
    filteredContactos.forEach((contacto) => {
      const id = contacto.categoria.id;
      if (!groups.has(id)) {
        groups.set(id, {
          categoria: {
            id,
            nombre: contacto.categoria.nombre,
            nombreKey: contacto.categoria.nombre.toLowerCase().trim(),
          },
          contactos: [],
        });
      }
      groups.get(id)!.contactos.push(contacto);
    });
    return Array.from(groups.values());
  }, [filteredContactos]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory(null);
  };

  const hasActiveFilters = searchQuery !== "" || selectedCategory !== null;

  const resultsLabel =
    filteredContactos.length === 1
      ? t("resultsOne", { count: filteredContactos.length })
      : t("resultsMany", { count: filteredContactos.length });

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      {!loading && contactos.length > 0 && (
        <section className="border-b border-gray-200 sticky top-0 z-30 bg-white/95 backdrop-blur">
          <div className="max-w-[80%] mx-auto py-5 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="relative w-full md:max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-10 py-2.5 bg-white border border-gray-200 rounded-full text-sm
                     focus:outline-none focus:border-[#102542] transition-colors text-gray-900 placeholder-gray-400"
                />
                {isSearching && searchQuery && (
                  <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#102542] animate-spin" />
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                    aria-label={t("clearFilters")}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {categorias.length > 0 && (
                <FilterDropdown
                  label={t("filter")}
                  value={selectedCategory}
                  options={categorias}
                  onChange={setSelectedCategory}
                  allLabel={t("allCategories")}
                />
              )}
            </div>

            <div className="flex items-center justify-between text-xs uppercase tracking-wider">
              <span className="text-gray-500 font-mono">{resultsLabel}</span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 text-[#102542] font-semibold hover:gap-2 transition-all"
                >
                  <X className="w-3.5 h-3.5" />
                  {t("clearFilters")}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Loading */}
      {loading && (
        <section className="max-w-[80%] mx-auto py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white p-6 space-y-3">
                <div className="h-5 w-5 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
                <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty */}
      {!loading && filteredContactos.length === 0 && (
        <section className="max-w-[80%] mx-auto py-24 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">
            {t("noResults")}
          </h3>
          <p className="text-gray-600 mb-6">{t("noResultsDescription")}</p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#102542] text-white rounded-full text-sm font-semibold hover:bg-[#0a1a30] transition-colors"
            >
              <X className="w-4 h-4" />
              {t("clearFilters")}
            </button>
          )}
        </section>
      )}

      {/* Groups */}
      {!loading && groupedContactos.length > 0 && (
        <section className="max-w-[80%] mx-auto py-16 space-y-16">
          {groupedContactos.map((group) => {
            const Icon = getCategoryIcon(group.categoria.nombre);
            return (
              <div key={group.categoria.id}>
                <div className="flex items-end justify-between gap-4 mb-8">
                  <div>
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-3">
                      <span className="w-8 h-px bg-[#102542]" />
                      <Icon className="w-3.5 h-3.5" />
                      {group.categoria.nombre}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                      {group.categoria.nombre}
                    </h2>
                  </div>
                  <span className="text-xs uppercase tracking-wider text-gray-400 font-mono whitespace-nowrap">
                    {group.contactos.length} {t("contacts")}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
                  {group.contactos.map((contacto) => (
                    <ContactCard key={contacto.id} contacto={contacto} />
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Stats */}
      {!loading && contactos.length > 0 && (
        <section className="max-w-[80%] mx-auto pb-24">
          <div className="max-w-3xl mb-14">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
              <span className="w-8 h-px bg-[#102542]" />
              {t("statsEyebrow")}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              {t("statsTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {categorias.map((cat) => {
              const count = contactos.filter(
                (c) => c.categoria.id === cat.id,
              ).length;
              const Icon = getCategoryIcon(cat.nombre);
              return (
                <div
                  key={cat.id}
                  className="bg-white p-8 flex flex-col justify-between min-h-[160px]"
                >
                  <Icon className="w-6 h-6 text-[#102542]" />
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-[#102542] tracking-tight tabular-nums">
                      {count}
                    </p>
                    <p className="text-sm text-gray-500 mt-2 leading-snug">
                      {cat.nombre}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}
